/**
 * Public certificate verification page — anonymous access.
 * Accessed via QR code on every Campus certificate.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, GraduationCap, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerifyCertificate() {
    const [params] = useSearchParams();
    const certNumber = params.get('n');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!certNumber) { setLoading(false); return; }
        axios.get(`${API}/campus/verify/${encodeURIComponent(certNumber)}`)
            .then(r => setResult(r.data))
            .catch(() => setResult({ valid: false, cert_number: certNumber }))
            .finally(() => setLoading(false));
    }, [certNumber]);

    return (
        <div className="min-h-screen bg-background flex flex-col" data-testid="verify-certificate-page">
            <header className="px-8 lg:px-16 py-6 border-b border-border">
                <Link to="/" className="inline-flex items-center gap-2.5" data-testid="brand-header">
                    <img src="/brand/african-valley-logo-256.png" alt="African Valley · Projet 360°"
                         className="h-9 w-9 object-contain rounded" />
                    <div className="leading-tight">
                        <div className="font-heading text-xl">African <span className="text-primary">Valley</span></div>
                        <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">Campus 360°</div>
                    </div>
                </Link>
            </header>
            <main className="flex-1 flex items-center justify-center px-6 py-12">
                <div className="max-w-xl w-full">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                            <ShieldCheck size={28} strokeWidth={1.5} />
                        </div>
                        <div className="eyebrow text-primary mb-2">Vérification de certificat</div>
                        <h1 className="font-heading text-4xl">Authenticité</h1>
                    </div>

                    {loading ? (
                        <div className="card-soft p-10 flex items-center justify-center gap-3 text-muted-foreground">
                            <Loader2 size={18} className="animate-spin" /> Vérification en cours…
                        </div>
                    ) : !certNumber ? (
                        <div className="card-soft p-10 text-center">
                            <div className="text-foreground/60">Aucun numéro de certificat fourni dans l'URL.</div>
                        </div>
                    ) : result?.valid ? (
                        <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/40 p-8" data-testid="verify-valid">
                            <div className="flex items-start gap-4 mb-6">
                                <div className="h-12 w-12 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
                                    <CheckCircle2 size={24} />
                                </div>
                                <div>
                                    <div className="text-emerald-700 font-heading text-2xl mb-1">Certificat authentique</div>
                                    <div className="text-sm text-emerald-700/80">Délivré officiellement par African Valley Campus 360°</div>
                                </div>
                            </div>
                            <dl className="space-y-3 text-sm">
                                <Row label="Bénéficiaire" value={result.learner_name || '—'} />
                                <Row label="Type" value={
                                    result.cert_type === 'core' ? '🎓 Tronc Commun · 10 piliers' :
                                    result.cert_type === 'pillar' ? '📚 Pilier individuel' :
                                    '🎯 Cas d\'usage sectoriel'
                                } />
                                <Row label="Parcours" value={result.reference_title || '—'} />
                                <Row label="Leçons validées" value={String(result.lessons_count ?? '—')} />
                                <Row label="Score moyen" value={`${result.avg_score ?? '—'} %`} />
                                <Row label="Numéro" value={<code className="text-xs">{result.cert_number}</code>} />
                                <Row label="Délivré le" value={result.issued_at ? new Date(result.issued_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'} />
                            </dl>
                        </div>
                    ) : (
                        <div className="rounded-2xl border-2 border-red-200 bg-red-50/40 p-8" data-testid="verify-invalid">
                            <div className="flex items-start gap-4 mb-3">
                                <div className="h-12 w-12 rounded-full bg-red-500 text-white flex items-center justify-center shrink-0">
                                    <XCircle size={24} />
                                </div>
                                <div>
                                    <div className="text-red-700 font-heading text-2xl mb-1">Certificat introuvable</div>
                                    <div className="text-sm text-red-700/80">Ce numéro ne correspond à aucun certificat émis par African Valley Campus 360°.</div>
                                </div>
                            </div>
                            <div className="mt-4 text-xs text-muted-foreground">
                                Numéro testé : <code>{certNumber}</code>
                            </div>
                        </div>
                    )}

                    <div className="mt-10 text-center text-xs text-muted-foreground">
                        Vérification 100% en ligne · <Link to="/" className="text-primary hover:underline">africanvalley.app</Link>
                    </div>
                </div>
            </main>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-baseline gap-4 border-b border-emerald-100/50 pb-2">
            <dt className="text-xs uppercase tracking-wider text-muted-foreground w-32 shrink-0">{label}</dt>
            <dd className="flex-1 text-foreground font-medium">{value}</dd>
        </div>
    );
}
